import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LightbulbIcon, TargetIcon, FlameIcon, GlobeIcon, BarChartIcon, FlaskConicalIcon, PaletteIcon, LockIcon } from 'lucide-react';
import { DjayAvatar, DjayEmotion, DjayVariant } from './DjayAvatar';
import { DjayBubble } from './DjayBubble';
import { useDjaySelection } from './useDjaySelection';
import { useAppStore } from '../app/hooks/useAppStore';
import { useMotionBudget } from '../app/hooks/useMotionBudget';
import { useClickOutside } from '../app/hooks/useClickOutside';
import { soundManager } from '../utils/soundManager';
import { dataLayer } from '../data/dataLayer';
import { MODULES } from '../content';
import { getDjayGreeting, getDjayViewMessage, getDjayDailyTip } from './djayMessages';
interface FloatingDjayProps {
  currentView?: string;
  onNavigate?: (path: string) => void;
}
export function FloatingDjay({
  currentView,
  onNavigate
}: FloatingDjayProps) {
  const {
    state,
    activeAccount,
    isDemoMode,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState<string | null>(null);
  const [bubbleAutoDismiss, setBubbleAutoDismiss] = useState<number | undefined>(undefined);
  const [emotion, setEmotion] = useState<DjayEmotion>('happy');
  const [isPulsing, setIsPulsing] = useState(false);
  const [tapPulse, setTapPulse] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isLongPressTriggered = useRef(false);
  const hasShownWelcomeRef = useRef(false);
  const lastViewRef = useRef<string | undefined>(currentView);
  const streak = state?.streak?.currentTotalStreak || 0;
  const displayName = activeAccount?.displayName || 'vriend';
  // Load easter egg state
  useEffect(() => {
    if (activeAccount?.id) {
      dataLayer.getDjayLongPressDiscovered(activeAccount.id).then(setHasDiscoveredLongPress);
    }
  }, [activeAccount?.id]);
  // Calculate unlock conditions
  const {
    masteredCount,
    totalQuestionsAnswered
  } = useMemo(() => {
    let mastered = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      totalQs += answered;
      if (mastery >= 95) mastered++;
    });
    return {
      masteredCount: mastered,
      totalQuestionsAnswered: totalQs
    };
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  const consecutiveFullWeeks = state?.dailyStreak?.consecutiveFullWeeks || 0;
  const unlockedVariants = useMemo(() => {
    return [{
      id: 'default' as DjayVariant,
      name: 'Standaard',
      unlocked: true
    }, {
      id: 'pixel' as DjayVariant,
      name: 'DJ Pixel',
      unlocked: totalQuestionsAnswered >= 100
    }, {
      id: 'blaze' as DjayVariant,
      name: 'DJ Blaze',
      unlocked: consecutiveFullWeeks >= 3
    }, {
      id: 'frost' as DjayVariant,
      name: 'DJ Frost',
      unlocked: masteredCount >= 3
    }, {
      id: 'royal' as DjayVariant,
      name: 'DJ Royal',
      unlocked: masteredCount >= MODULES.length
    }, {
      id: 'cosmic' as DjayVariant,
      name: 'DJ Cosmic',
      unlocked: (state?.stats?.level || 0) >= 15
    }, {
      id: 'shadow' as DjayVariant,
      name: 'DJ Shadow',
      unlocked: (state?.stats?.xp || 0) >= 30000
    }, {
      id: 'ninja' as DjayVariant,
      name: 'DJ Ninja',
      unlocked: hasDiscoveredLongPress
    }];
  }, [totalQuestionsAnswered, consecutiveFullWeeks, masteredCount, state?.stats?.level, state?.stats?.xp, hasDiscoveredLongPress]);
  useClickOutside(panelRef, () => {
    if (isPanelOpen) setIsPanelOpen(false);
    if (isLongPressMenuOpen) setIsLongPressMenuOpen(false);
  });
  // Helper to show a proactive message with an attention pulse first
  const showProactiveMessage = (msg: string, newEmotion: DjayEmotion, duration: number) => {
    if (isPanelOpen || isLongPressMenuOpen) return;
    setIsPulsing(true);
    setEmotion(newEmotion);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => {
      setIsPulsing(false);
      setBubbleMessage(msg);
      setBubbleAutoDismiss(duration);
      soundManager.playDjaySpeak();
    }, 1000);
  };
  // 1. Welcome on first load
  useEffect(() => {
    if (!hasShownWelcomeRef.current && activeAccount) {
      hasShownWelcomeRef.current = true;
      const greeting = getDjayGreeting(displayName);
      setTimeout(() => {
        showProactiveMessage(greeting.text, greeting.emotion, 4000);
      }, 1000);
    }
  }, [activeAccount, displayName]);
  // 2. View change reactions
  useEffect(() => {
    if (currentView && currentView !== lastViewRef.current && currentView !== 'practice') {
      lastViewRef.current = currentView;
      const viewMsg = getDjayViewMessage(currentView, {
        streak,
        displayName
      });
      showProactiveMessage(viewMsg.text, viewMsg.emotion, 3000);
    }
  }, [currentView, streak, displayName]);
  // 3. Idle detection (2 minutes)
  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        showProactiveMessage('Zullen we verder gaan?', 'thinking', 5000);
      }, 2 * 60 * 1000);
    };
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    resetIdle();
    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);
  const handlePointerDown = () => {
    isLongPressTriggered.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressTriggered.current = true;
      setIsPanelOpen(false);
      setIsLongPressMenuOpen(true);
      // Easter egg logic
      if (!hasDiscoveredLongPress && activeAccount?.id) {
        setHasDiscoveredLongPress(true);
        dataLayer.saveDjayLongPressDiscovered(activeAccount.id, true);
        setBubbleMessage('Geheim menu gevonden! Je hebt DJ Ninja ontgrendeld! 🥷');
        setBubbleAutoDismiss(5000);
        setEmotion('celebrating');
      } else {
        setBubbleMessage(null);
        setEmotion('excited');
      }
      soundManager.playDjaySpeak();
      // Haptic feedback if available
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(50);
      }
    }, 500); // 500ms for long press
  };
  const handlePointerUp = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  };
  const handleDjayClick = () => {
    if (isLongPressTriggered.current) return; // Skip if it was a long press
    setTapPulse(true);
    setTimeout(() => setTapPulse(false), 500);
    if (isLongPressMenuOpen) {
      setIsLongPressMenuOpen(false);
      return;
    }
    if (isPanelOpen) {
      setIsPanelOpen(false);
      return;
    }
    setBubbleMessage(null);
    setIsPulsing(false);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    const viewMsg = getDjayViewMessage(currentView || 'home', {
      streak,
      displayName
    });
    setEmotion(viewMsg.emotion);
    setIsPanelOpen(true);
    soundManager.playDjaySpeak();
  };
  const handleTipClick = () => {
    const tip = getDjayDailyTip();
    setEmotion(tip.emotion);
    setIsPanelOpen(false);
    setBubbleMessage(tip.text);
    setBubbleAutoDismiss(5000);
    soundManager.playDjaySpeak();
  };
  const handleNavClick = (path: string) => {
    setIsLongPressMenuOpen(false);
    setIsPanelOpen(false);
    if (onNavigate) onNavigate(path);
  };
  if (currentView === 'practice' || !activeAccount && !isDemoMode) {
    return null;
  }
  const greetingMsg = getDjayViewMessage(currentView || 'home', {
    streak,
    displayName
  });
  return <motion.div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 flex items-end justify-end pointer-events-none" initial={shouldAnimate ? {
    y: 100,
    opacity: 0
  } : false} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    damping: 15,
    stiffness: 200
  }} data-id="element-2953">
      <div className="relative flex items-end pointer-events-auto" ref={panelRef} data-id="element-2954">
        {/* Expanded Click Panel */}
        <AnimatePresence data-id="element-2955">
          {isPanelOpen && !isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.2
        }} className="absolute bottom-full right-0 mb-4 w-64 md:w-72 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right" data-id="element-2956">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-2957" />

              <div className="relative z-10" data-id="element-2958">
                <p className="text-slate-800 font-medium mb-3 text-sm md:text-base" data-id="element-2959">
                  {greetingMsg.text}
                </p>

                <div className="space-y-2" data-id="element-2960">
                  <button onClick={handleTipClick} className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-sm font-medium transition-colors" data-id="element-2961">
                    <LightbulbIcon className="w-4 h-4" data-id="element-2962" />
                    Dagelijkse tip
                  </button>

                  <button onClick={() => handleNavClick('learningLab')} disabled={currentView !== 'home'} className="w-full flex items-center gap-2 px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-orange-50" data-id="element-2963">
                    <TargetIcon className="w-4 h-4" data-id="element-2964" />
                    Aanbeveling
                  </button>

                  <div className="w-full flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-medium" data-id="element-2965">
                    <FlameIcon className="w-4 h-4 text-orange-500" data-id="element-2966" />
                    Mijn streak: {streak} dagen
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Long Press Quick Nav Menu */}
        <AnimatePresence data-id="element-2967">
          {isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.2
        }} className="absolute bottom-full right-0 mb-4 w-72 bg-white rounded-3xl shadow-2xl border border-slate-100 p-5 origin-bottom-right" data-id="element-2968">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-2969" />

              <div className="relative z-10" data-id="element-2970">
                <div className="flex items-center justify-between mb-4" data-id="element-2971">
                  <p className="text-slate-800 font-bold text-base" data-id="element-2972">Snelmenu</p>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-slate-100 text-slate-500 rounded-full" data-id="element-2973">
                    Geheim
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-5" data-id="element-2974">
                  <button onClick={() => handleNavClick('subjects')} className="flex flex-col items-center justify-center gap-2 p-3 bg-blue-50/50 hover:bg-blue-50 rounded-2xl transition-colors border border-blue-100/50" data-id="element-2975">
                    <GlobeIcon className="w-6 h-6 text-blue-500" data-id="element-2976" />
                    <span className="text-xs font-semibold text-blue-900" data-id="element-2977">
                      Werelden
                    </span>
                  </button>
                  <button onClick={() => handleNavClick('progress')} className="flex flex-col items-center justify-center gap-2 p-3 bg-emerald-50/50 hover:bg-emerald-50 rounded-2xl transition-colors border border-emerald-100/50" data-id="element-2978">
                    <BarChartIcon className="w-6 h-6 text-emerald-500" data-id="element-2979" />
                    <span className="text-xs font-semibold text-emerald-900" data-id="element-2980">
                      Voortgang
                    </span>
                  </button>
                  <button onClick={() => handleNavClick('learningLab')} className="flex flex-col items-center justify-center gap-2 p-3 bg-purple-50/50 hover:bg-purple-50 rounded-2xl transition-colors col-span-2 border border-purple-100/50" data-id="element-2981">
                    <FlaskConicalIcon className="w-6 h-6 text-purple-500" data-id="element-2982" />
                    <span className="text-xs font-semibold text-purple-900" data-id="element-2983">
                      Trainingslab
                    </span>
                  </button>
                </div>

                <div className="border-t border-slate-100 pt-4" data-id="element-2984">
                  <div className="flex items-center gap-1.5 mb-3" data-id="element-2985">
                    <PaletteIcon className="w-4 h-4 text-slate-400" data-id="element-2986" />
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2987">
                      Djay stijl
                    </p>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide px-1" data-id="element-2988">
                    {unlockedVariants.map(v => <button key={v.id} onClick={() => v.unlocked && setSelectedVariant(v.id)} disabled={!v.unlocked} className={`relative w-12 h-12 rounded-full flex-shrink-0 transition-all flex items-center justify-center
                          ${selectedVariant === v.id ? 'ring-2 ring-offset-2 ring-orange-500 scale-110 bg-orange-50' : 'bg-slate-50'}
                          ${v.unlocked ? 'hover:scale-110 hover:bg-slate-100 cursor-pointer' : 'opacity-60 cursor-not-allowed grayscale'}
                        `} title={v.unlocked ? v.name : `${v.name} (Vergrendeld)`} aria-label={`Kies ${v.name} stijl`} data-id="element-2989">
                        <div className="w-8 h-8" data-id="element-2990">
                          <DjayAvatar variant={v.id} emotion="happy" size="sm" data-id="element-2991" />
                        </div>
                        {!v.unlocked && <div className="absolute inset-0 bg-slate-900/10 rounded-full flex items-center justify-center backdrop-blur-[1px]" data-id="element-2992">
                            <LockIcon className="w-4 h-4 text-slate-600 drop-shadow-sm" data-id="element-2993" />
                          </div>}
                      </button>)}
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Simple Bubble (Proactive or Tip) */}
        <div className="absolute bottom-full right-4 mb-2 md:mb-4 origin-bottom-right" data-id="element-2994">
          <DjayBubble message={bubbleMessage || ''} isVisible={!!bubbleMessage && !isPanelOpen && !isLongPressMenuOpen} onDismiss={() => setBubbleMessage(null)} autoDismissMs={bubbleAutoDismiss} position="bottom" data-id="element-2995" />
        </div>

        {/* Avatar Button */}
        <motion.button onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} onClick={handleDjayClick} className="relative focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300/50 rounded-full select-none" aria-label="Praat met Djay" whileHover={shouldAnimate ? {
        scale: 1.05
      } : undefined} whileTap={shouldAnimate ? {
        scale: 0.9,
        y: 4
      } : undefined} animate={shouldAnimate && !isPanelOpen && !isLongPressMenuOpen && !bubbleMessage ? {
        y: [0, -4, 0],
        transition: {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      } : undefined} style={{
        willChange: 'transform',
        touchAction: 'none'
      }} data-id="element-2996">
          {/* Tap Pulse */}
          <AnimatePresence data-id="element-2997">
            {tapPulse && <motion.div initial={{
            opacity: 0.8,
            scale: 1
          }} animate={{
            opacity: 0,
            scale: 1.5
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="absolute inset-0 border-4 border-orange-300 rounded-full" data-id="element-2998" />}
          </AnimatePresence>

          {/* Attention Pulse Glow */}
          <AnimatePresence data-id="element-2999">
            {isPulsing && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: [0.4, 0.8, 0.4],
            scale: [1, 1.2, 1]
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="absolute inset-0 bg-orange-400 rounded-full blur-md" data-id="element-3000" />}
          </AnimatePresence>

          {/* Shadow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/10 rounded-full blur-sm" data-id="element-3001" />

          <div className="w-12 h-12 md:w-16 md:h-16 relative z-10" data-id="element-3002">
            <DjayAvatar emotion={emotion} variant={selectedVariant} size="xl" className="w-full h-full drop-shadow-md" animate={shouldAnimate} data-id="element-3003" />
          </div>
        </motion.button>
      </div>
    </motion.div>;
}